/**
 * Tema de graficos derivado de los tokens CSS del sistema de diseno. Los colores nunca se
 * fijan en codigo: se leen de las variables (`--chart-N`, `--foreground`...) del tema
 * activo y se convierten a `rgb()` porque ECharts no interpreta `oklch()`.
 */
export interface ChartTheme {
  readonly palette: readonly string[];
  readonly text: string;
  readonly mutedText: string;
  readonly border: string;
  readonly selection: string;
  readonly surface: string;
}

const PALETTE_SIZE = 8;

/** Lee el tema de las variables CSS del elemento (o del documento). */
export function resolveChartTheme(element?: Element): ChartTheme {
  const target = element ?? globalThis.document.documentElement;
  const style = getComputedStyle(target);
  const read = (name: string): string => toRgb(style.getPropertyValue(name).trim());
  return {
    palette: Array.from({ length: PALETTE_SIZE }, (_, i) =>
      read(`--chart-${String(i + 1)}`),
    ).filter((color) => color !== ''),
    text: read('--foreground'),
    mutedText: read('--muted-foreground'),
    border: read('--border'),
    selection: read('--selection'),
    surface: read('--popover'),
  };
}

const OKLCH = /^oklch\(\s*([\d.]+%?)\s+([\d.]+%?)\s+([\d.]+)(?:deg)?\s*(?:\/\s*([\d.]+%?))?\s*\)$/i;

const parseComponent = (text: string, percentScale: number): number =>
  text.endsWith('%') ? (Number(text.slice(0, -1)) / 100) * percentScale : Number(text);

/** Convierte `oklch(L C H [/ A])` a `rgb()`/`rgba()`; otros formatos se devuelven tal cual. */
export function toRgb(color: string): string {
  const match = OKLCH.exec(color);
  if (!match) return color;
  const lightness = parseComponent(match[1] ?? '0', 1);
  const chroma = parseComponent(match[2] ?? '0', 0.4);
  const hue = (Number(match[3] ?? '0') * Math.PI) / 180;
  const alpha = match[4] === undefined ? 1 : parseComponent(match[4], 1);

  // OKLCH -> OKLab -> LMS -> sRGB lineal -> sRGB (Bjorn Ottosson).
  const a = chroma * Math.cos(hue);
  const b = chroma * Math.sin(hue);
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const linear = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  const [red, green, blue] = linear.map((channel) => {
    const clamped = Math.min(1, Math.max(0, channel));
    const encoded = clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055;
    return Math.round(encoded * 255);
  });
  return alpha >= 1
    ? `rgb(${String(red)}, ${String(green)}, ${String(blue)})`
    : `rgba(${String(red)}, ${String(green)}, ${String(blue)}, ${String(Math.round(alpha * 1000) / 1000)})`;
}
